/**
 * AI Service Layer Type Definitions
 * Provides provider-agnostic interfaces for AI operations
 */

/**
 * Base message interface for chat interactions
 */
export interface Message {
  role: 'system' | 'user' | 'assistant' | 'function' | 'tool';
  content: string;
  name?: string;
  function_call?: {
    name: string;
    arguments: string;
  };
  tool_calls?: ToolCall[];
}

/**
 * Tool call interface for function execution
 */
export interface ToolCall {
  id: string;
  type: 'function';
  function: {
    name: string;
    arguments: string;
  };
}

/**
 * Model capabilities declaration
 */
export interface ModelCapabilities {
  chat: boolean;
  completion: boolean;
  tools: boolean;
  vision: boolean;
  streaming: boolean;
  maxTokens: number;
  contextWindow: number;
}

/**
 * AI model definition
 */
export interface AIModel {
  id: string;
  name: string;
  provider: string;
  capabilities: ModelCapabilities;
  costPer1kTokens?: {
    input: number;
    output: number;
  };
}

/**
 * Parameters for text generation
 */
export interface GenerateTextParams {
  prompt: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
  stopSequences?: string[];
  systemPrompt?: string;
}

/**
 * Parameters for object generation (structured output)
 */
export interface GenerateObjectParams<T = any> {
  prompt: string;
  schema: object; // JSON Schema
  model?: string;
  temperature?: number;
  maxTokens?: number;
  systemPrompt?: string;
}

/**
 * Parameters for streaming text generation
 */
export interface StreamTextParams {
  prompt: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
  stopSequences?: string[];
  systemPrompt?: string;
  onChunk?: (chunk: TextChunk) => void;
}

/**
 * Text generation response
 */
export interface TextResponse {
  text: string;
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  finishReason?: 'stop' | 'length' | 'content_filter' | 'tool_calls';
}

/**
 * Object generation response
 */
export interface ObjectResponse<T = any> {
  object: T;
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

/**
 * Streaming text chunk
 */
export interface TextChunk {
  text: string;
  isFirst: boolean;
  isLast: boolean;
}

/**
 * Chat-specific options
 */
export interface ChatOptions {
  model?: string;
  temperature?: number;
  maxTokens?: number;
  tools?: Tool[];
  toolChoice?: 'auto' | 'none' | { type: 'function'; function: { name: string } };
  systemPrompt?: string;
  stream?: boolean;
}

/**
 * Chat response
 */
export interface ChatResponse {
  message: Message;
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  finishReason?: 'stop' | 'length' | 'content_filter' | 'tool_calls';
}

/**
 * Completion-specific options
 */
export interface CompletionOptions {
  model?: string;
  temperature?: number;
  maxTokens?: number;
  stopSequences?: string[];
  stream?: boolean;
}

/**
 * Completion response
 */
export interface CompletionResponse {
  text: string;
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  finishReason?: 'stop' | 'length' | 'content_filter';
}

/**
 * Tool definition for function calling
 */
export interface Tool {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: object; // JSON Schema
  };
}

/**
 * Tool execution request
 */
export interface ToolRequest {
  tools: Tool[];
  messages: Message[];
  model?: string;
  toolChoice?: 'auto' | 'none' | { type: 'function'; function: { name: string } };
}

/**
 * Tool execution response
 */
export interface ToolResponse {
  toolCalls: ToolCall[];
  message: Message;
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

/**
 * AI provider interface
 */
export interface AIProvider {
  id: string;
  name: string;
  models: AIModel[];
  
  // Core operations
  generateText(params: GenerateTextParams): Promise<TextResponse>;
  generateObject<T = any>(params: GenerateObjectParams<T>): Promise<ObjectResponse<T>>;
  streamText(params: StreamTextParams): AsyncIterableIterator<TextChunk>;
  
  // Chat operations
  chat(messages: Message[], options?: ChatOptions): Promise<ChatResponse>;
  streamChat(messages: Message[], options?: ChatOptions): AsyncIterableIterator<ChatResponse>;
  
  // Tool operations
  executeTools(request: ToolRequest): Promise<ToolResponse>;
  
  // Utility methods
  validateApiKey(): Promise<boolean>;
  getModelInfo(modelId: string): AIModel | undefined;
  listModels(): AIModel[];
}

/**
 * AI configuration interface
 */
export interface AIConfig {
  providers: {
    openai?: {
      apiKey: string;
      baseUrl?: string;
      defaultModel?: string;
      organization?: string;
    };
    anthropic?: {
      apiKey: string;
      baseUrl?: string;
      defaultModel?: string;
    };
    xai?: {
      apiKey: string;
      baseUrl?: string;
      defaultModel?: string;
    };
  };
  defaultProvider: string;
  defaultModel?: string;
  temperature?: number;
  maxTokens?: number;
  retryAttempts?: number;
  retryDelay?: number;
  timeout?: number;
}

/**
 * AI service interface (main entry point)
 */
export interface AIService {
  // Core operations
  chat(messages: Message[], options?: ChatOptions): Promise<ChatResponse>;
  complete(prompt: string, options?: CompletionOptions): Promise<CompletionResponse>;
  streamChat(messages: Message[], options?: ChatOptions): AsyncIterableIterator<ChatResponse>;
  streamComplete(prompt: string, options?: CompletionOptions): AsyncIterableIterator<string>;
  
  // Tool operations
  executeTools(request: ToolRequest): Promise<ToolResponse>;
  
  // Configuration
  configure(config: Partial<AIConfig>): void;
  getConfig(): AIConfig;
  
  // Provider management
  switchProvider(providerId: string): void;
  switchModel(modelId: string): void;
  getCurrentProvider(): AIProvider;
  getCurrentModel(): AIModel;
  listProviders(): AIProvider[];
  listModels(): AIModel[];
  
  // MCP-ready methods
  serialize(): string;
  deserialize(data: string): void;
  
  // Utility methods
  validateConfiguration(): Promise<{ valid: boolean; errors: string[] }>;
  estimateCost(tokens: { input: number; output: number }): number | undefined;
}

/**
 * Provider registry interface
 */
export interface ProviderRegistry {
  register(provider: AIProvider): void;
  unregister(providerId: string): void;
  get(providerId: string): AIProvider | undefined;
  list(): AIProvider[];
  getDefault(): AIProvider | undefined;
  setDefault(providerId: string): void;
}

/**
 * Error types for AI operations
 */
export enum AIErrorType {
  PROVIDER_NOT_FOUND = 'PROVIDER_NOT_FOUND',
  MODEL_NOT_FOUND = 'MODEL_NOT_FOUND',
  API_KEY_INVALID = 'API_KEY_INVALID',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  CONTEXT_LENGTH_EXCEEDED = 'CONTEXT_LENGTH_EXCEEDED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  INVALID_RESPONSE = 'INVALID_RESPONSE',
  CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',
}

/**
 * AI operation error
 */
export class AIError extends Error {
  constructor(
    public type: AIErrorType,
    message: string,
    public providerId?: string,
    public modelId?: string,
    public cause?: unknown
  ) {
    super(message);
    this.name = 'AIError';
  }
}